import { useState } from "react";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import LeadForm from "@/components/energy/LeadForm";
import { ToolShell, NumInput, ErrorBox, ResultGrid, EstimateNote, sle } from "@/components/energy/tool";
import { api, formatApiError } from "@/lib/solarApi";

export default function EnergyFinancing() {
  const [form, setForm] = useState({ system_cost_sle: "", deposit_sle: "", months: 12, annual_rate_pct: 0 });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showLead, setShowLead] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const compute = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/energy/financing", { ...form, deposit_sle: form.deposit_sle || 0 });
      setResult(data.result);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Seo title="Make Solar Affordable — Financing Estimates | BNM" description="Illustrative solar payment options in Leones: deposit and monthly payment estimates. Financing terms are confirmed by BNM." />
      <ToolShell
        testid="financing-tool"
        eyebrow="Make Solar Affordable"
        title="Illustrative payment options."
        lead="See what a deposit and monthly plan could look like. All figures are illustrative until financing terms are confirmed by BNM — nothing here is an approved or guaranteed offer."
      >
        <div className="rounded-lg border border-slate-200 bg-white p-6 sm:p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <NumInput label="System cost" suffix="SLE" min={0} testid="fin-cost" value={form.system_cost_sle} onChange={set("system_cost_sle")} hint="Use the estimated total from your Energy Plan" />
            <NumInput label="Deposit" suffix="SLE" min={0} testid="fin-deposit" value={form.deposit_sle} onChange={set("deposit_sle")} />
            <NumInput label="Number of months" min={1} max={84} step={1} testid="fin-months" value={form.months} onChange={set("months")} />
            <NumInput label="Financing rate (optional)" suffix="% / year" min={0} max={60} testid="fin-rate" value={form.annual_rate_pct} onChange={set("annual_rate_pct")} />
          </div>
          {error && <div className="mt-5"><ErrorBox msg={error} /></div>}
          <button onClick={compute} disabled={busy} data-testid="fin-compute"
            className="mt-7 inline-flex items-center gap-2 rounded-md bg-obsidian px-7 py-3.5 font-semibold text-white hover:bg-pine hover:text-volt transition-colors disabled:opacity-60">
            {busy ? "Calculating…" : "Estimate Payments"} <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {result && (
          <div className="mt-10 space-y-8" data-testid="financing-results">
            <ResultGrid items={[
              { label: "Estimated deposit", value: sle(result.deposit_sle) },
              { label: "Estimated monthly payment", value: sle(result.monthly_sle), hint: `for ${result.months} months` },
              { label: "Estimated total payment", value: sle(result.total_sle) },
            ]} />
            <EstimateNote />
            <button onClick={() => setShowLead(true)} data-testid="financing-cta"
              className="rounded-md bg-obsidian px-7 py-3.5 font-semibold text-white hover:bg-pine hover:text-volt transition-colors">
              Request Financing Information
            </button>
            {showLead && <LeadForm type="financing" title="Request Financing Information" context={{ monthly_sle: result.monthly_sle, months: result.months }} defaultMessage="I would like to discuss payment options for a solar system." />}
          </div>
        )}
      </ToolShell>
    </>
  );
}
